/*
 * The draft-key format, in both directions. Every text surface in every module persists under one
 * of these keys (architecture.md §11 item 16), so the format is a contract; this file is the only
 * place that knows it, and the server's `(targetKind, targetId)` pair maps onto it here too.
 *
 * A key's prefix — what stands before the first colon — is also what decides how an outbox item
 * written under it leaves the device (`transport.ts`): chat's keys go out over its socket, an AI
 * chat's over its HTTP routes, and neither side needs to know the other exists.
 */

import type { TargetKey } from "./types.ts"

export type Target =
	| { kind: "conversation"; conv: number }
	| { kind: "thread"; conv: number; threadRootId: number }
	| { kind: "messageEdit"; messageId: number }
	| { kind: "aichat"; chatId: number }
	| { kind: "aichatQueued"; itemId: number }
	| { kind: "aichatCard"; itemId: number }
	| { kind: "aichatNew"; id: string }
	| { kind: "docThread"; docId: number }
	| { kind: "docReply"; threadId: number }
	| { kind: "docCommentEdit"; commentId: number }

/**
 * The server mirror's address of a target (protocol.md §2.5.6 `DraftView.targetKind`). Only chat's
 * drafts are mirrored: an AI chat's and a document comment's live on this device alone.
 */
export interface WireTarget {
	targetKind: "conversation" | "thread" | "messageEdit"
	targetId: number
}

const CONVERSATION = /^c:(\d+)$/
const THREAD = /^c:(\d+):t:(\d+)$/
const EDIT = /^e:(\d+)$/
/** The three AI chat keys that name a row by its id: the chat, a queued prompt, a card. */
const AICHAT = /^(ai|aiq|aik):(\d+)$/
/** A new AI chat's key: the characters of a uuid, and nothing a key could be confused by. */
const AICHAT_NEW = /^ain:([0-9a-f-]{8,64})$/
/** The three document comment keys: a new comment on a document, a reply in a thread, an edit. */
const DOC_COMMENT = /^(dt|dr|de):(\d+)$/

/** Which AI chat target a prefix names, for the three keys that carry a number. */
const AICHAT_KINDS = { ai: "aichat", aiq: "aichatQueued", aik: "aichatCard" } as const

function id(s: string): number | null {
	const n = Number(s)
	return Number.isSafeInteger(n) && n > 0 ? n : null
}

export function keyOf(t: Target): TargetKey {
	switch (t.kind) {
		case "conversation":
			return `c:${t.conv}`
		case "thread":
			return `c:${t.conv}:t:${t.threadRootId}`
		case "messageEdit":
			return `e:${t.messageId}`
		case "aichat":
			return `ai:${t.chatId}`
		case "aichatQueued":
			return `aiq:${t.itemId}`
		case "aichatCard":
			return `aik:${t.itemId}`
		case "aichatNew":
			return `ain:${t.id}`
		case "docThread":
			return `dt:${t.docId}`
		case "docReply":
			return `dr:${t.threadId}`
		case "docCommentEdit":
			return `de:${t.commentId}`
	}
}

/** The target a key names, or null for anything that is not a well-formed key. */
export function parseKey(s: string): Target | null {
	let m = CONVERSATION.exec(s)
	if (m?.[1] !== undefined) {
		const conv = id(m[1])
		return conv === null ? null : { kind: "conversation", conv }
	}
	m = THREAD.exec(s)
	if (m?.[1] !== undefined && m[2] !== undefined) {
		const conv = id(m[1])
		const threadRootId = id(m[2])
		return conv === null || threadRootId === null ? null : { kind: "thread", conv, threadRootId }
	}
	m = EDIT.exec(s)
	if (m?.[1] !== undefined) {
		const messageId = id(m[1])
		return messageId === null ? null : { kind: "messageEdit", messageId }
	}
	m = AICHAT.exec(s)
	if (m?.[1] !== undefined && m[2] !== undefined) {
		const n = id(m[2])
		if (n === null) return null
		const kind = AICHAT_KINDS[m[1] as keyof typeof AICHAT_KINDS]
		return kind === "aichat" ? { kind, chatId: n } : { kind, itemId: n }
	}
	m = AICHAT_NEW.exec(s)
	if (m?.[1] !== undefined) return { kind: "aichatNew", id: m[1] }
	m = DOC_COMMENT.exec(s)
	if (m?.[1] !== undefined && m[2] !== undefined) {
		const n = id(m[2])
		if (n === null) return null
		if (m[1] === "dt") return { kind: "docThread", docId: n }
		return m[1] === "dr"
			? { kind: "docReply", threadId: n }
			: { kind: "docCommentEdit", commentId: n }
	}
	return null
}

export function isTargetKey(s: string): s is TargetKey {
	return parseKey(s) !== null
}

/**
 * The conversation a key writes into, or null for an edit — a message id says nothing about it —
 * and for every key that is not a chat conversation's.
 */
export function conversationOf(key: TargetKey): number | null {
	const t = parseKey(key)
	return t !== null && (t.kind === "conversation" || t.kind === "thread") ? t.conv : null
}

/**
 * The prefix of a key — the part before the first colon — which is what an outbox transport is
 * registered under (`transport.ts`): `c`, `e`, `ai`, `aiq`, `aik`, `ain`, `dt`, `dr`, `de`.
 */
export function prefixOf(key: TargetKey): string {
	return key.slice(0, key.indexOf(":"))
}

/** The server mirror's address of a target, or null for one the mirror does not keep. */
export function wireTarget(t: Target): WireTarget | null {
	switch (t.kind) {
		case "conversation":
			return { targetKind: t.kind, targetId: t.conv }
		case "thread":
			return { targetKind: t.kind, targetId: t.threadRootId }
		case "messageEdit":
			return { targetKind: t.kind, targetId: t.messageId }
		case "aichat":
		case "aichatQueued":
		case "aichatCard":
		case "aichatNew":
		case "docThread":
		case "docReply":
		case "docCommentEdit":
			return null
	}
}
